import { useRef, useState, type CSSProperties, type KeyboardEvent, type MouseEvent, type PointerEvent } from "react";
import { Link } from "react-router";
import { Briefgeist, type IconTyp } from "../design/Briefgeist";
import { HALTEN_MS, HALTEN_SPIEL } from "../seiten/wischen";
import { abzeichenText } from "./abzeichen";

export type Reiter = {
  name: string;
  symbol: IconTyp;
  /** Ziel des Reiters (Schale); ohne Ziel ein Knopf (Vorschau) */
  zu?: string;
  /** Abzeichen: zu prüfen plus überfällig (plan-w82.md 3.6) */
  abzeichen?: { pruefen: number; ueberfaellig: number };
  /** Reiter „Gaist“: Glyphe des Geists mit Funke statt eines Symbols; `antwortet`, solange eine Antwort entsteht */
  gaist?: boolean;
  antwortet?: boolean;
};

/** Sind die Namen der Leiste verborgen (große Schrift, Container `tab-pille` bis 18em, oder kompakt)? Gelesen am Namen des Reiters */
function nameVerborgen(reiter: HTMLElement): boolean {
  const name = reiter.querySelector(".tab-pille-name");
  return !!name && getComputedStyle(name).clipPath !== "none";
}

/** Tab-Leiste als schwebende Glas-Pille (E2): `nav.tab-leiste` mit data-welt und Unschärfe (data-schwebend), in der Schale 60
 *  hoch, Reiter 50, 16 über dem sicheren Rand (D-1148; Vorlage 70, Ecke 35); das Licht unter dem aktiven Reiter gleitet (lebhaft Feder, ruhig expo). In der Schale sind die Reiter Links
 *  mit `aria-current`; der Name steht im DOM vor Symbol und Abzeichen, damit er vorne steht („Start 2 zu prüfen,
 *  1 überfällig“), sichtbar bleibt das Symbol oben (W52b); das Abzeichen trägt den Lesetext wie der Zähler des Bestands. */
export function TabPille(props: { reiter: Reiter[]; aktiv: number; onWahl?: (index: number) => void; label?: string; className?: string; kompakt?: boolean; onVoll?: () => void }) {
  // W82 Web-6a-N1 (WN-28, D-1148 Weg B, wie iOS Large Content Viewer): sind die Namen verborgen, steht der Name des Reiters groß
  // über der Leiste, bei Tastaturfokus (`:focus-visible`) sofort bis zum Fokusverlust oder Escape, beim Zeiger erst nach Halten
  // (`HALTEN_MS` bei höchstens `HALTEN_SPIEL`); Loslassen über demselben Reiter löst ihn aus, Wegziehen blendet aus, ohne
  // auszulösen; ein kurzer Tipp navigiert wie bisher. Die Anzeige ist `aria-hidden`, ohne Fokus und Zeigerereignisse
  const [gross, setGross] = useState<number | null>(null);
  const halten = useRef<{ i: number; x: number; y: number; uhr: ReturnType<typeof setTimeout> | null; gezeigt: boolean } | null>(null);
  const klickWeg = useRef(false);
  const selbst = useRef(false);
  const loesen = () => {
    if (halten.current?.uhr) clearTimeout(halten.current.uhr);
    halten.current = null;
  };
  const drin = (el: HTMLElement, e: PointerEvent) => {
    const r = el.getBoundingClientRect();
    return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
  };
  // weggezogen: ausblenden, beim Loslassen nichts auslösen
  const weggezogen = () => {
    setGross(null);
    klickWeg.current = true;
    loesen();
  };
  const gesten = (i: number) => ({
    onFocus: (e: { currentTarget: HTMLElement }) => {
      if (e.currentTarget.matches(":focus-visible") && nameVerborgen(e.currentTarget)) setGross(i);
    },
    onBlur: () => setGross((g) => (g === i ? null : g)),
    onKeyDown: (e: KeyboardEvent) => {
      klickWeg.current = false;
      if (e.key === "Escape") setGross((g) => (g === i ? null : g));
    },
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      klickWeg.current = false;
      loesen();
      if (e.button !== 0 || !nameVerborgen(e.currentTarget)) return;
      const h = { i, x: e.clientX, y: e.clientY, uhr: null as ReturnType<typeof setTimeout> | null, gezeigt: false };
      h.uhr = setTimeout(() => {
        h.gezeigt = true;
        setGross(i);
      }, HALTEN_MS);
      halten.current = h;
    },
    onPointerMove: (e: PointerEvent<HTMLElement>) => {
      const h = halten.current;
      if (!h || h.i !== i) return;
      if (!h.gezeigt) {
        if (Math.hypot(e.clientX - h.x, e.clientY - h.y) > HALTEN_SPIEL) loesen();
      } else if (!drin(e.currentTarget, e)) weggezogen();
    },
    // Maus: das Verlassen des Reiters (Touch hält den Zeiger am Reiter fest, dort prüft `onPointerMove` die Lage)
    onPointerLeave: () => {
      const h = halten.current;
      if (h?.i === i) {
        if (h.gezeigt) weggezogen();
        else loesen();
      }
    },
    onPointerUp: (e: PointerEvent<HTMLElement>) => {
      const h = halten.current;
      if (!h || h.i !== i) return;
      loesen();
      if (!h.gezeigt) return;
      setGross(null);
      // nach dem Halten löst das Loslassen über demselben Reiter aus (ein eigener Klick), ein folgender Klick des Browsers nicht
      // noch einmal
      if (drin(e.currentTarget, e)) {
        selbst.current = true;
        e.currentTarget.click();
      }
      klickWeg.current = true;
      setTimeout(() => (klickWeg.current = false), 400);
    },
    onPointerCancel: () => {
      if (halten.current?.gezeigt) setGross(null);
      loesen();
    },
    onClick: (e: MouseEvent) => {
      if (selbst.current) {
        selbst.current = false;
        return;
      }
      if (!klickWeg.current) return;
      klickWeg.current = false;
      e.preventDefault();
    },
    // langes Drücken auf einem Link öffnet sonst Linkvorschau oder Kontextmenü
    onContextMenu: (e: MouseEvent<HTMLElement>) => {
      if (nameVerborgen(e.currentTarget)) e.preventDefault();
    },
  });
  const anzeige = gross !== null ? props.reiter[gross] : undefined;
  const AnzeigeSymbol = anzeige?.symbol;
  return (
    <nav
      className={`tab-leiste tab-pille ${props.className ?? ""}`}
      // HW11 (Entwurf hinter `VITE_W82_REITER_KOMPAKT`): zusammengezogen beim Rollen nach unten, ein Antippen macht sie voll
      data-kompakt={props.kompakt ? "" : undefined}
      onPointerDown={props.kompakt ? props.onVoll : undefined}
      data-welt=""
      data-schwebend=""
      aria-label={props.label ?? "Hauptnavigation"}
      style={{ "--reiter": Math.max(0, props.aktiv), "--reiter-zahl": props.reiter.length } as CSSProperties}
    >
      <div className="tab-pille-reihe tab-raster">
        <span className="tab-pille-licht" aria-hidden="true" data-aus={props.aktiv < 0 ? "" : undefined} />
        {props.reiter.map((r, i) => {
          const Symbol = r.symbol;
          const zahl = r.abzeichen ? r.abzeichen.pruefen + r.abzeichen.ueberfaellig : 0;
          const inhalt = (
            <>
              <span className="tab-pille-name tab-label w-reiter" data-name={r.name}>
                {r.name}
              </span>
              <span className="tab-pille-ikon">
                {r.gaist ? <Briefgeist linie size={24} aria-hidden aktiv={r.antwortet} /> : <Symbol size={24} strokeWidth={1.75} aria-hidden />}
                {zahl > 0 && r.abzeichen && (
                  <span className="tab-pille-abzeichen" aria-label={abzeichenText(r.abzeichen)} data-testid="abzeichen">
                    {zahl > 99 ? "99+" : zahl}
                  </span>
                )}
              </span>
            </>
          );
          const aktuell = i === props.aktiv ? "page" : undefined;
          return r.zu ? (
            <Link key={r.name} to={r.zu} className="tab-pille-reiter w-reiter" aria-current={aktuell} {...gesten(i)}>
              {inhalt}
            </Link>
          ) : (
            <button
              key={r.name}
              type="button"
              className="tab-pille-reiter w-reiter"
              aria-current={aktuell}
              {...gesten(i)}
              onClick={(e) => {
                gesten(i).onClick(e);
                if (!e.defaultPrevented) props.onWahl?.(i);
              }}
            >
              {inhalt}
            </button>
          );
        })}
      </div>
      {anzeige && AnzeigeSymbol && (
        <div className="tab-pille-gross glas t-title-3" aria-hidden="true" data-reiter={anzeige.name}>
          {anzeige.gaist ? <Briefgeist linie size={28} aria-hidden /> : <AnzeigeSymbol size={28} strokeWidth={1.75} aria-hidden />}
          <span className="tab-pille-gross-name">{anzeige.name}</span>
        </div>
      )}
    </nav>
  );
}
